import { z } from 'zod'

import { fileTypeSchema, optionSchema } from '@/validations/schemas'

const isHTMLEmpty = (html: string): boolean => {
  if (!html) return true
  const tempDiv = document.createElement('div')
  tempDiv.innerHTML = html
  const text = tempDiv.textContent || tempDiv.innerText || ''
  return text.trim().length === 0
}

export const basicInfosSchema = z
  .object({
    banner: z.array(fileTypeSchema).optional(),
    name: z.string().min(1, { message: 'Nome é obrigatório' }),
    startDate: z.date().refine((date) => !Number.isNaN(date.getTime()), {
      message: 'Data inválida',
    }),
    endDate: z
      .date()
      .refine(
        (date) => {
          if (!date) return true
          return !Number.isNaN(date.getTime())
        },
        {
          message: 'Data inválida',
        },
      )
      .optional(),
    withoutEndDate: z.boolean().default(true),
    summary: z.string().min(1, { message: 'Resumo é obrigatório' }),
    attachments: z.array(fileTypeSchema),
    description: z
      .string()
      .min(1, { message: 'Descrição é obrigatória' })
      .refine(
        (value) => {
          if (typeof window === 'undefined') return true
          return !isHTMLEmpty(value)
        },
        {
          message: 'Descrição é obrigatória',
        },
      ),
    strategicsObjective: z
      .string()
      .min(1, {
        message: 'Objetivo estratégico é obrigatório',
      })
      .refine(
        (value) => {
          if (typeof window === 'undefined') return true
          return !isHTMLEmpty(value)
        },
        {
          message: 'Objetivo estratégico é obrigatório',
        },
      ),
  })
  .refine(
    (data) => {
      if (!data.endDate) return true

      return data.startDate.getTime() < data.endDate.getTime()
    },
    {
      message: 'Data de início deve ser anterior à data de fim',
      path: ['endDate'],
    },
  )
  .refine(
    (data) => {
      if (data.withoutEndDate) return true

      return !!data.endDate
    },
    {
      message: 'Data de fim é obrigatória quando não marcado "Sem prazo"',
      path: ['endDate'],
    },
  )

export const accessConfigurationSchema = z.object({
  challengeResponsible: optionSchema
    .extend({
      avatar: z.string().optional().nullable(),
      area: z.string().optional().nullable(),
    })
    .optional(),
  experts: z
    .array(
      optionSchema.extend({
        avatar: z.string().optional().nullable(),
        area: z.string().optional().nullable(),
      }),
    )
    .optional(),
  organizationAreas: z.array(optionSchema).optional(),
})

export const criteriaActiveSchema = z.object({
  id: z.string().optional(),
  name: z.string().min(1, { message: 'Nome é obrigatório' }),
  description: z.string().optional(),
  step: z.string().min(1, { message: 'Etapa é obrigatória' }),
  weight: z.string().min(1, { message: 'Peso é obrigatório' }),
  mandatory: z.boolean(),
  scales: z
    .array(
      z.object({
        name: z.string().min(1, { message: 'Nome é obrigatório' }),
      }),
    )
    .min(1, { message: 'Escala é obrigatória' }),
})

export const criteriaSchema = z
  .object({
    usingCriteria: z.boolean().default(false),
    rules: z.array(
      z.object({
        id: z.string().optional(),
        stepId: z.string(),
        name: z.string().min(1, { message: 'Nome é obrigatório' }),
        evaluators: z
          .number({ required_error: 'Número de avaliadores é obrigatório' })
          .or(z.undefined())
          .transform((val) => (val === undefined ? 0 : val))
          .pipe(
            z
              .number()
              .min(0, {
                message: 'Número de avaliadores não pode ser menor que 0',
              })
              .max(15, {
                message: 'Número de avaliadores deve ser menor que 15',
              }),
          ),
      }),
    ),
    actives: z.array(criteriaActiveSchema),
  })
  .refine(
    (data) => {
      if (!data.usingCriteria) return true

      return data.actives?.length
    },
    {
      message: 'Atividades do Critério são obrigatórias',
      path: ['actives'],
    },
  )
  .refine(
    (data) => {
      if (!data.usingCriteria) return true

      return data.rules?.length >= 4
    },
    {
      message: 'Regras do Critério são obrigatórias',
      path: ['rules'],
    },
  )

export const workflowTaskResponsibleSchema = z.object({
  id: z.string(),
  name: z.string(),
  avatar: z.string().nullable().optional(),
  area: z.string().nullable().optional(),
})

export const workflowTaskSchema = z.object({
  id: z.string().optional(),
  name: z
    .string()
    .min(4, {
      message: 'Nome da tarefa deve ter no mínimo 4 caracteres',
    })
    .max(60, {
      message: 'Nome da tarefa deve ter no máximo 60 caracteres',
    }),
  sequence: z.number().optional(),
  endDate: z.date().optional(),
  responsible: workflowTaskResponsibleSchema.optional(),
})
export const workflowStepSchema = z
  .object({
    id: z.string().optional(),
    name: z
      .string()
      .min(1, { message: 'Nome da etapa é obrigatório' })
      .max(60, {
        message: 'Nome da etapa deve ter no máximo 60 caracteres',
      }),
    sequence: z.number().optional(),
    tasks: z.array(workflowTaskSchema),
    attachments: z.any().optional().default([]),
  })
  .refine(
    (data) => {
      const taskNames = data.tasks.map((task) => task.name.toLowerCase().trim())
      const uniqueTaskNames = new Set(taskNames)
      return taskNames.length === uniqueTaskNames.size
    },
    {
      message: 'Não é possível criar tarefas com o mesmo nome no estágio',
      path: ['tasks'],
    },
  )

export const workflowSchema = z.object({
  id: z.string().optional(),
  sequence: z.number().optional(),
  name: z
    .string()
    .min(1, { message: 'Nome do fluxo de trabalho é obrigatório' }),
  steps: z.array(workflowStepSchema).min(1, { message: 'Etapa é obrigatória' }),
})

export const ideaFieldSchema = z
  .object({
    id: z.string().optional(),
    name: z.string().min(1, { message: 'Nome é obrigatório' }),
    description: z.string().optional(),
    format: z.enum(['TEXT', 'TEXTAREA', 'SELECT'], {
      message: 'Formato inválido',
    }),
    formatOptions: z
      .array(
        z.object({
          name: z.string().min(1, { message: 'Nome da opção é obrigatório' }),
        }),
      )
      .optional(),
    mandatory: z.boolean(),
    disabled: z.boolean().default(false),
    position: z.number().optional(),
  })
  .refine(
    (data) => {
      if (data.format === 'SELECT') {
        const formatOptionsLength = data.formatOptions?.length ?? 0
        return formatOptionsLength >= 2
      }

      return true
    },
    {
      message:
        'Se o formato for "Seleção Única", é necessário adicionar pelo menos duas opções',
      path: ['formatOptions'],
    },
  )

export const challengeSchema = z
  .object({
    basicInfos: basicInfosSchema,
    accessConfiguration: accessConfigurationSchema,
    criteria: criteriaSchema,
    workflows: z.array(workflowSchema),
    ideaFields: z.array(ideaFieldSchema),
  })
  .refine(
    (data) => {
      const names = data.ideaFields.map((field) =>
        field.name.toLowerCase().trim(),
      )
      const uniqueNames = new Set(names)
      return names.length === uniqueNames.size
    },
    {
      message: 'Não é possível criar campos com o mesmo nome',
      path: ['ideaFields'],
    },
  )
  .refine(
    (data) => {
      return data.workflows.length > 0
    },
    {
      message: 'É necessário criar pelo menos um fluxo de trabalho',
      path: ['workflows'],
    },
  )
